import { SchoolClass } from '../types';

/**
 * Custom sorting function for class names
 * Order: Nursery, LKG, UKG, then numeric classes (1, 2, 3, ..., 11, 12)
 */
export const sortClasses = (classes: SchoolClass[]): SchoolClass[] => {
  return [...classes].sort((a, b) => {
    const getClassOrder = (className: string): number => {
      const normalized = className.toLowerCase().trim();

      // Handle pre-primary classes
      if (normalized === 'nursery') return 1;
      if (normalized === 'lkg') return 2;
      if (normalized === 'ukg') return 3;

      // Handle numeric classes - extract number from various formats
      const numericMatch = normalized.match(/(\d+)/);
      if (numericMatch) {
        const num = parseInt(numericMatch[1]);
        if (num >= 1 && num <= 12) {
          return 3 + num; // Start after UKG (3), so Class 1 = 4, Class 2 = 5, etc.
        }
      }

      // Handle other formats
      if (normalized.includes('1st') || normalized.includes('first')) return 4;
      if (normalized.includes('2nd') || normalized.includes('second')) return 5;
      if (normalized.includes('3rd') || normalized.includes('third')) return 6;
      if (normalized.includes('4th') || normalized.includes('fourth')) return 7;
      if (normalized.includes('5th') || normalized.includes('fifth')) return 8;
      if (normalized.includes('6th') || normalized.includes('sixth')) return 9;
      if (normalized.includes('7th') || normalized.includes('seventh')) return 10;
      if (normalized.includes('8th') || normalized.includes('eighth')) return 11;
      if (normalized.includes('9th') || normalized.includes('ninth')) return 12;
      if (normalized.includes('10th') || normalized.includes('tenth')) return 13;
      if (normalized.includes('11th') || normalized.includes('eleventh')) return 14;
      if (normalized.includes('12th') || normalized.includes('twelfth')) return 15;

      // Unknown classes go to the end
      return 1000;
    };

    const orderA = getClassOrder(a.name);
    const orderB = getClassOrder(b.name);

    return orderA - orderB;
  });
};

/**
 * Same ordering as sortClasses, for plain class-name strings
 * (e.g. names derived from student records rather than SchoolClass docs).
 */
export const sortClassNames = (names: string[]): string[] => {
  return [...names].sort((a, b) => getClassDisplayOrder(a) - getClassDisplayOrder(b));
};

/**
 * Get display order number for a class name (useful for debugging)
 */
export const getClassDisplayOrder = (className: string): number => {
  const normalized = className.toLowerCase().trim();

  if (normalized === 'nursery') return 1;
  if (normalized === 'lkg') return 2;
  if (normalized === 'ukg') return 3;

  const numericMatch = normalized.match(/(\d+)/);
  if (numericMatch) {
    const num = parseInt(numericMatch[1]);
    if (num >= 1 && num <= 12) {
      return 3 + num;
    }
  }

  if (normalized.includes('1st') || normalized.includes('first')) return 4;
  if (normalized.includes('2nd') || normalized.includes('second')) return 5;
  if (normalized.includes('3rd') || normalized.includes('third')) return 6;
  if (normalized.includes('4th') || normalized.includes('fourth')) return 7;
  if (normalized.includes('5th') || normalized.includes('fifth')) return 8;
  if (normalized.includes('6th') || normalized.includes('sixth')) return 9;
  if (normalized.includes('7th') || normalized.includes('seventh')) return 10;
  if (normalized.includes('8th') || normalized.includes('eighth')) return 11;
  if (normalized.includes('9th') || normalized.includes('ninth')) return 12;
  if (normalized.includes('10th') || normalized.includes('tenth')) return 13;
  if (normalized.includes('11th') || normalized.includes('eleventh')) return 14;
  if (normalized.includes('12th') || normalized.includes('twelfth')) return 15;

  return 1000;
};